import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProviderCard from "@/components/ProviderCard";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MapPin, Search as SearchIcon } from "lucide-react";

export default function Search() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState({ categories: {}, services: [] });
  const [category, setCategory] = useState("all");
  const [service, setService] = useState("");
  const [radius, setRadius] = useState([25]);
  const [coords, setCoords] = useState(null);
  const [providers, setProviders] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/catalog").then(({ data }) => setCatalog(data));
    if (user && user.role === "client") {
      api.get("/favorites").then(({ data }) => setFavIds(data.map((p) => p.id))).catch(() => {});
    }
  }, [user]);

  const geolocate = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success("Using your location");
      },
      () => toast.error("Location unavailable — showing all providers")
    );
  };

  const runSearch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (service) params.service = service;
      if (category !== "all") params.category = category;
      if (coords) { params.lat = coords.lat; params.lng = coords.lng; params.radius = radius[0]; }
      const { data } = await api.get("/providers", { params });
      setProviders(data);
      if (!data.length) toast.info("No providers found. Try widening your search.");
    } catch {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const toggleFav = async (id) => {
    if (!user) return toast.error("Login to save favorites");
    try {
      if (favIds.includes(id)) {
        await api.delete(`/favorites/${id}`);
        setFavIds(favIds.filter((f) => f !== id));
      } else {
        await api.post(`/favorites/${id}`);
        setFavIds([...favIds, id]);
        toast.success("Saved to favorites");
      }
    } catch { toast.error("Could not update favorite"); }
  };

  const servicesInCat =
    category === "all" ? catalog.services : catalog.services.filter((s) => s.category === category);

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-slate-900">
        Find <span className="text-[#9b111e]">Mobile Professionals</span> Near You
      </h1>

      <div className="card-metal p-6 mt-6 grid md:grid-cols-4 gap-4 items-end">
        <div>
          <Label>Category</Label>
          <Select value={category} onValueChange={(v) => { setCategory(v); setService(""); }}>
            <SelectTrigger className="mt-1 bg-white" data-testid="search-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.keys(catalog.categories).map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Service</Label>
          <Select value={service || "any"} onValueChange={(v) => setService(v === "any" ? "" : v)}>
            <SelectTrigger className="mt-1 bg-white" data-testid="search-service"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="any">Any Service</SelectItem>
              {servicesInCat.map((s) => (
                <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Within {radius[0]} miles</Label>
          <Slider min={5} max={100} step={5} value={radius} onValueChange={setRadius}
            className="mt-3" data-testid="search-radius" />
        </div>

        <div className="flex gap-2">
          <button onClick={geolocate} data-testid="search-geo-btn" className="btn-silver-3d flex-1 !py-2.5 !px-3">
            <MapPin className="w-4 h-4 text-[#9b111e]" />
            <span className="text-engraved text-xs">{coords ? "✓" : "Locate"}</span>
          </button>
          <button onClick={runSearch} data-testid="search-submit-btn" className="btn-red-3d flex-1 !py-2.5 !px-3">
            <SearchIcon className="w-4 h-4" /> Search
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-center py-16 font-heading">Searching…</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-testid="provider-results">
          {providers.map((p) => (
            <ProviderCard key={p.id} provider={p} isFavorite={favIds.includes(p.id)} onToggleFavorite={toggleFav} />
          ))}
        </div>
      )}
    </div>
  );
}
